import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang, useSeo } from "../i18n/LangContext";
import { LineReveal, Reveal, Eyebrow } from "../components/site/Reveal";
import { HeroBlocks } from "../components/site/HeroBlocks";
import { Marquee } from "../components/site/Marquee";
import { QuitandaSeal } from "../components/site/Mark";
import { FinalCta } from "../components/site/Sections";
import { EASE } from "../config";

const Hero = ({ t }) => (
  <section className="relative mx-auto grid max-w-[1400px] gap-10 px-6 pb-16 pt-32 sm:px-10 lg:min-h-[100svh] lg:grid-cols-12 lg:items-center lg:pb-10 lg:pt-28" data-testid="hero-section">
    <div className="relative z-10 lg:col-span-8">
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
        <Eyebrow>{t.hero.eyebrow}</Eyebrow>
      </motion.p>
      <LineReveal
        testId="hero-title"
        lines={t.hero.lines}
        className="f-serif mt-8 text-[clamp(3.1rem,8.2vw,8.25rem)] font-normal leading-[0.92] tracking-[-0.035em]"
        lastClassName="italic text-[#005F60]"
      />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.7 }}>
        <p className="mt-10 max-w-xl text-lg leading-relaxed text-[#2E353B]" data-testid="hero-subtitle">{t.hero.sub}</p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/o-que-fazemos" className="btn-dark" data-testid="hero-institutions-btn">{t.hero.inst} <ArrowRight size={16} /></Link>
          <Link to="/editora-quitanda#autores" className="btn-line" data-testid="hero-authors-btn">{t.hero.auth} <ArrowRight size={16} /></Link>
        </div>
      </motion.div>
    </div>
    <div className="relative h-[300px] sm:h-[420px] lg:col-span-4 lg:h-[62vh]">
      <HeroBlocks />
    </div>
  </section>
);

const Credentials = ({ t }) => (
  <section className="mx-auto max-w-[1400px] px-6 py-16 sm:px-10 lg:py-24" data-testid="credentials-strip">
    <Reveal><Eyebrow>{t.creds.eyebrow}</Eyebrow></Reveal>
    <div className="mt-10 grid grid-cols-1 border-t border-[#121518] sm:grid-cols-2 lg:grid-cols-4">
      {t.creds.items.map((c, i) => (
        <Reveal key={c.big} delay={i * 0.08} className="border-b border-[#E2DDD5] py-8 sm:pr-8 lg:border-b-0 lg:border-r lg:px-8 lg:first:pl-0 lg:last:border-r-0">
          <p className="f-serif text-5xl tracking-tight text-[#005F60] lg:text-6xl" data-testid={`credential-${i}`}>{c.big}</p>
          <p className="mt-4 max-w-[16rem] text-sm leading-relaxed text-[#2E353B]">{c.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const accents = ["#FF682F", "#005F60", "#FFCC00"];

const Pillars = ({ t }) => (
  <section className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 lg:py-36" data-testid="home-pillars">
    <div className="grid gap-8 lg:grid-cols-12">
      <Reveal className="lg:col-span-4"><Eyebrow>{t.pillarsIntro.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.1} className="lg:col-span-8">
        <h2 className="f-serif text-[clamp(2rem,4.4vw,3.8rem)] leading-[1.05] tracking-[-0.02em]">{t.pillarsIntro.title}</h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#2E353B]">{t.pillarsIntro.text}</p>
      </Reveal>
    </div>
    <div className="mt-16 grid gap-5 md:grid-cols-3">
      {t.pillars.map((p, i) => (
        <Reveal key={p.id} delay={i * 0.1} className="h-full">
          <Link to={`/o-que-fazemos#${p.id}`} data-testid={`pillar-card-${p.id}`} className="group relative flex h-full min-h-[420px] flex-col overflow-hidden border border-[#E2DDD5] bg-white p-8 transition-colors duration-500 hover:border-[#121518] lg:p-10">
            <span className="absolute right-0 top-0 h-16 w-16 origin-top-right transition-transform duration-700 ease-out group-hover:scale-[2.2]" style={{ background: accents[i] }} aria-hidden="true" />
            <span className="f-serif text-sm italic text-[#4A525A]">{p.num}</span>
            <h3 className="f-serif mt-auto text-4xl leading-none tracking-tight lg:text-5xl">{p.name}</h3>
            <p className="mt-5 text-sm leading-relaxed text-[#2E353B]">{p.short}</p>
            <span className="mt-8 inline-flex items-center gap-2 text-[13px] font-semibold tracking-wide text-[#005F60]">
              {t.pillarsIntro.more} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const QuitandaBlock = ({ t }) => (
  <section className="relative overflow-hidden bg-[#0E1A1B] text-[#FAF8F5]" data-testid="home-quitanda-block">
    <div className="absolute -right-10 top-0 hidden h-full w-[24%] bg-[#FF682F] [clip-path:polygon(35%_0,100%_0,100%_100%,0_100%)] md:block" aria-hidden="true" />
    <div className="absolute bottom-0 right-[8%] hidden h-28 w-28 bg-[#FFCC00] md:block" aria-hidden="true" />
    <div className="relative mx-auto max-w-[1400px] px-6 py-24 sm:px-10 lg:py-36">
      <div className="max-w-2xl">
        <Reveal><Eyebrow light>{t.quitandaBlock.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1} className="mt-10"><QuitandaSeal sig={t.quitandaBlock.sig} size="lg" dark /></Reveal>
        <Reveal delay={0.2}>
          <p className="f-serif mt-10 max-w-md text-2xl leading-snug text-[#E9E4DA]">{t.quitandaBlock.text}</p>
          <Link to="/editora-quitanda" className="btn-light mt-10 inline-flex" data-testid="home-quitanda-link">
            {t.quitandaBlock.link} <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  const { t } = useLang();
  useSeo("home");
  return (
    <>
      <Hero t={t} />
      <Credentials t={t} />
      <Marquee words={t.marquee} />
      <Pillars t={t} />
      <QuitandaBlock t={t} />
      <FinalCta />
    </>
  );
}
